import React, { useMemo } from 'react'

import { getValueFormat } from '@baurine/grafana-value-formats'
import {
  Alert,
  Box,
  Card,
  Radio,
  Stack,
  Title,
  Tooltip,
  Typography
} from '@tidbcloud/uikit'
import { MRT_ColumnDef, ProTable } from '@tidbcloud/uikit/biz'

import { formatValue } from '../../SQLRepository/migration_shared/format-value'
import { StatementV2Model } from '../types'

interface PlansListProps {
  data: StatementV2Model[]
  selectedPlanDigest?: string
  showRU: boolean
  onSelect: (planDigest: string) => void
}

const formatRU = (value?: number) =>
  typeof value === 'number' ? getValueFormat('short')(value, 2, 2) : ''

export const PlansList: React.FC<PlansListProps> = ({
  data,
  selectedPlanDigest,
  showRU,
  onSelect
}) => {
  const columns = useMemo<MRT_ColumnDef<StatementV2Model>[]>(() => {
    const result: MRT_ColumnDef<StatementV2Model>[] = [
      {
        id: 'selected',
        header: '',
        size: 48,
        enableSorting: false,
        accessorFn: (row) => row.plan_digest ?? '',
        Cell: ({ row }) => {
          const planDigest = row.original.plan_digest
          return (
            <Radio
              name='statement-v2-execution-plan'
              size='xs'
              aria-label={`Select plan ${planDigest ?? ''}`}
              checked={selectedPlanDigest === planDigest}
              disabled={!planDigest}
              onChange={() => planDigest && onSelect(planDigest)}
            />
          )
        }
      },
      {
        id: 'plan_digest',
        header: 'Plan ID',
        enableSorting: false,
        accessorFn: (row) => (
          <Tooltip label={row.plan_digest || 'N/A'} withArrow>
            <Typography className='wd-truncate' maw={240}>
              {row.plan_digest || 'N/A'}
            </Typography>
          </Tooltip>
        )
      },
      {
        id: 'sum_latency',
        header: 'Total Latency',
        accessorFn: (row) => formatValue(row, 'sum_latency', 'ns')
      },
      {
        id: 'avg_latency',
        header: 'Mean Latency',
        accessorFn: (row) => formatValue(row, 'avg_latency', 'ns')
      },
      {
        id: 'exec_count',
        header: 'Executions Count',
        accessorFn: (row) => formatValue(row, 'exec_count', 'short')
      }
    ]

    if (showRU) {
      result.push(
        {
          id: 'sum_ru',
          header: 'Total RU',
          accessorFn: (row) => formatRU(row.sum_ru)
        },
        {
          id: 'avg_ru',
          header: 'Mean RU',
          accessorFn: (row) => formatRU(row.avg_ru)
        }
      )
    } else {
      result.push({
        id: 'avg_mem',
        header: 'Mean Memory',
        accessorFn: (row) => formatValue(row, 'avg_mem', 'bytes')
      })
    }

    return result
  }, [onSelect, selectedPlanDigest, showRU])

  const sortedData = useMemo(
    () =>
      [...data].sort(
        (left, right) => (right.exec_count ?? 0) - (left.exec_count ?? 0)
      ),
    [data]
  )

  return (
    <Card shadow='none' p='xl'>
      <Stack gap='xs'>
        <Title order={5}>Execution Plans</Title>
        {data.length > 1 && (
          <Alert>
            Select one execution plan to view its execution details.
          </Alert>
        )}

        <Box
          sx={(theme) => ({
            border: `1px solid ${theme.colors.carbon[4]}`,
            borderRadius: theme.defaultRadius,
            overflow: 'hidden'
          })}
        >
          <ProTable<StatementV2Model>
            columns={columns}
            data={sortedData}
            enableSorting={false}
          />
        </Box>
      </Stack>
    </Card>
  )
}
